import { useState } from 'react';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import type { ScopedSamples } from '@dealstack/shared/lib/documents/fieldBindingCatalogue.ts';
import { ResolveToCard } from './ResolveToCard.tsx';
import { DataPointCatalogue } from './DataPointCatalogue.tsx';
import { ListBindingCard } from './ListBindingCard.tsx';
import { SecurityScopeCard } from './SecurityScopeCard.tsx';
import { initialBindingMode } from './bindingBuilder.utils.ts';
import { useSingleBinding } from './useSingleBinding.ts';
import { useListBinding } from './useListBinding.ts';
import { useSecurityBinding } from './useSecurityBinding.ts';
import { isBindingMode } from './bindPanel.utils.ts';
import { ModeToggle } from './bindPanel.styled.ts';
import type {
	BindingMode,
	DataPointWithSampleRes,
	JoinWith,
	SecurityOptionRes,
} from './BindPanel.types.ts';

interface Props {
	/** The Liquid expression the builder seeds from (a saved binding), or null
	 * for a fresh binding. The builder is uncontrolled after mount — remount it
	 * (via `key`) to re-seed from a different value. */
	bindingTemplate: string | null;
	catalogue: DataPointWithSampleRes[];
	/** Called with the rebuilt Liquid expression whenever the selection or
	 * formatting changes. The owner decides what to do with it (persist a field
	 * binding, insert/replace an inline token, …). */
	onChange: (bindingTemplate: string) => void;
	/** Programme securities offered by the "per security" mode. Defaults to []
	 * — the global template editor has no programme, so it never offers that
	 * mode, and its existing call site passes none of these three props. */
	securities?: SecurityOptionRes[];
	/** Per-security sample values for the scoped catalogue's preview, keyed by
	 * security id, then source, then relative path. Missing entries render
	 * empty by construction, so an absent one is not an error. */
	scopedSamples?: ScopedSamples;
	/** Repoints every already-scoped binding in the document at one security.
	 * Omitted by owners that hold only one binding (the template editor). */
	onApplySecurityToAll?: (securityId: string) => void;
	/** The programme's data switches that are off. What reads data behind one is not offered,
	 * but a saved binding that already holds it still shows it. Omitted where there is no
	 * programme. */
	uncollectedRequirements?: readonly string[];
}

const NO_UNCOLLECTED_REQUIREMENTS: readonly string[] = [];

/**
 * Builds one Liquid binding expression, in whichever of three modes fits the blank.
 *
 * Each mode owns its own state and its own template-building, in its own hook — see
 * `useSingleBinding`, `useListBinding`, `useSecurityBinding`. This component holds only what
 * they share: the mode toggle, the catalogue search box and the join-with labels. Switching
 * mode calls the incoming mode's `apply()`, so the persisted binding always matches the
 * controls on screen.
 */
export const BindingBuilder = ({
	bindingTemplate,
	catalogue,
	onChange,
	securities = [],
	scopedSamples = {},
	onApplySecurityToAll,
	uncollectedRequirements = NO_UNCOLLECTED_REQUIREMENTS,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	// Shared: single and security mode each render a catalogue, and it is the same search box.
	const [search, setSearch] = useState('');
	const [bindingMode, setBindingMode] = useState<BindingMode>(() =>
		initialBindingMode(bindingTemplate)
	);

	const single = useSingleBinding({
		bindingTemplate,
		catalogue,
		uncollectedRequirements,
		offersSecurityScope: securities.length > 0,
		search,
		onChange,
	});
	const list = useListBinding({ bindingTemplate, uncollectedRequirements, onChange });
	const security = useSecurityBinding({
		bindingTemplate,
		securities,
		scopedSamples,
		search,
		onChange,
	});

	const joinWithOptions: { value: JoinWith; label: string }[] = [
		{ value: 'space', label: tMipManage('master_documents.field_binding.panel.join.space') },
		{ value: 'comma', label: tMipManage('master_documents.field_binding.panel.join.comma') },
		{
			value: 'newline',
			label: tMipManage('master_documents.field_binding.panel.join.newline'),
		},
		{ value: 'none', label: tMipManage('master_documents.field_binding.panel.join.none') },
	];

	const changeBindingMode = (next: BindingMode) => {
		setBindingMode(next);
		// Rewrite the template to match the newly active mode, so the persisted binding always
		// reflects what the visible controls show.
		if (next === 'list') {
			list.apply();
		} else if (next === 'security') {
			security.apply();
		} else {
			single.apply();
		}
	};

	// A saved scoped template opened where no programme securities exist (the global template
	// editor) must not render an unusable card with an empty picker — fall back to single mode.
	const activeMode: BindingMode =
		bindingMode === 'security' && securities.length === 0 ? 'single' : bindingMode;

	return (
		<>
			<ModeToggle
				fullWidth
				size="sm"
				value={bindingMode}
				onChange={(value) => {
					if (isBindingMode(value)) {
						changeBindingMode(value);
					}
				}}
				data={[
					{
						value: 'single',
						label: tMipManage('master_documents.field_binding.list.mode.single'),
					},
					...(securities.length > 0
						? [
								{
									value: 'security',
									label: tMipManage(
										'master_documents.field_binding.security.mode'
									),
								},
							]
						: []),
					{
						value: 'list',
						label: tMipManage('master_documents.field_binding.list.mode.list'),
					},
				]}
			/>

			{activeMode === 'list' && (
				<ListBindingCard
					collections={list.collections}
					collection={list.collection}
					offeredItemFields={list.offeredItemFields}
					selectedPaths={list.itemPaths}
					fieldSeparator={list.fieldSeparator}
					itemSeparator={list.itemSeparator}
					preview={list.preview}
					onChangeCollection={list.onChangeCollection}
					onToggleItemField={list.onToggleItemField}
					onRemoveItemField={list.onRemoveItemField}
					onChangeFieldSeparator={list.onChangeFieldSeparator}
					onChangeItemSeparator={list.onChangeItemSeparator}
				/>
			)}

			{activeMode === 'security' && (
				<>
					<SecurityScopeCard
						securities={securities}
						securityId={security.securityId}
						transactionScope={security.transactionScope}
						onChangeSecurity={security.onChangeSecurity}
						onChangeTransactionScope={security.onChangeTransactionScope}
						onApplyToAll={onApplySecurityToAll}
					/>

					<ResolveToCard
						selectedDataPoints={security.selectedDataPoints}
						preview={security.preview}
						joinWith={security.joinWith}
						joinWithOptions={joinWithOptions}
						onRemoveDataPoint={security.onRemoveDataPoint}
						onChangeJoinWith={security.onChangeJoinWith}
					/>

					<DataPointCatalogue
						search={search}
						onSearchChange={setSearch}
						groups={security.groups}
						selectedPaths={security.selectedPaths}
						onToggleDataPoint={security.onToggleDataPoint}
					/>
				</>
			)}

			{activeMode === 'single' && (
				<>
					<ResolveToCard
						selectedDataPoints={single.selectedDataPoints}
						preview={single.preview}
						joinWith={single.joinWith}
						joinWithOptions={joinWithOptions}
						onRemoveDataPoint={single.onRemoveDataPoint}
						onChangeJoinWith={single.onChangeJoinWith}
					/>

					<DataPointCatalogue
						search={search}
						onSearchChange={setSearch}
						groups={single.groups}
						selectedPaths={single.selectedPaths}
						onToggleDataPoint={single.onToggleDataPoint}
					/>
				</>
			)}
		</>
	);
};
